import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import { Bq5BWMS } from "../components/Bq5BWMS";
import { Bq5Ballast } from "../components/Bq5Ballast";
import { Bq5DeckAndHatches } from "../components/Bq5DeckAndHatches";
import { Bq5Holds } from "../components/Bq5Holds";
import {
  Bq5CargoArrangementsM,
  initialBq5CargoArrangements,
  mapSqlToBq5CargoArrangementsForm,
} from "../models/bq5-cargo-arrangements.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF5CargoArrangementsAtom } from "../state/baltic-form.atoms";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq5CargoArrangements: React.FC<Props> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF5CargoArrangementsAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq5CargoArrangementsM>(initialBq5CargoArrangements);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));

    const parsedValue = Number(value);
    const num = name === "numberOfHolds" ? Math.min(parsedValue || 1, 9) : parsedValue;
    const numhatches = name === "numberOfHatches" ? Math.min(parsedValue || 1, 20) : parsedValue;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "numberOfHolds") {
        updated.holdDimensions = Array.from({ length: num }, (_, i) => ({
          holdNumber: i + 1,
          dimensionl: prev.holdDimensions?.[i]?.dimensionl || "",
          dimensionh: prev.holdDimensions?.[i]?.dimensionh || "",
          dimensionw: prev.holdDimensions?.[i]?.dimensionw || "",
          comments: prev.holdDimensions?.[i]?.comments || "",
        }));
        updated.holdCapacities = Array.from({ length: num }, (_, i) => ({
          holdNumber: i + 1,
          grainCapacity: prev.holdCapacities?.[i]?.grainCapacity || "",
          baleCapacity: prev.holdCapacities?.[i]?.baleCapacity || "",
        }));
        updated.tanktopsStrengths = Array.from({ length: num }, (_, i) => ({
          holdNumber: i + 1,
          strength: prev.tanktopsStrengths?.[i]?.strength || "",
        }));
        updated.floorMeasurements = Array.from({ length: num }, (_, i) => ({
          holdNumber: i + 1,
          floorMeasurement: prev.floorMeasurements?.[i]?.floorMeasurement || "",
        }));
      }
      if (name === "numberOfHatches") {
        updated.hatchDimensions = Array.from({ length: numhatches }, (_, i) => ({
          hatchNumber: i + 1,
          dimensionl: prev.hatchDimensions?.[i]?.dimensionl || "",
          dimensionw: prev.hatchDimensions?.[i]?.dimensionw || "",
        }));
        updated.hatchCoverStrengths = Array.from({ length: numhatches }, (_, i) => ({
          hatchNumber: i + 1,
          strength: prev.hatchCoverStrengths?.[i]?.strength || "",
        }));
        updated.cementFeederHoles = Array.from({ length: numhatches }, (_, i) => ({
          hatchNumber: i + 1,
          numberDiameter: prev.cementFeederHoles?.[i]?.numberDiameter || "",
          location: prev.cementFeederHoles?.[i]?.location || "",
        }));
      }

      return updated;
    });
  };

  const handleHoldDimensionChange = (index: number, field: string, value: string) => {
    setForm((prev) => {
      const updated = [...prev.holdDimensions];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return { ...prev, holdDimensions: updated };
    });
  };

  const handleHoldCapacityChange = (index: number, field: "grainCapacity" | "baleCapacity", value: string) => {
    setForm((prev) => {
      const updated = [...prev.holdCapacities];
      updated[index][field] = value;
      return { ...prev, holdCapacities: updated };
    });
  };

  const handleTanktopsStrengthsChange = (index: number, value: string) => {
    setForm((prev) => {
      const updated = [...prev.tanktopsStrengths];
      updated[index].strength = value;
      return { ...prev, tanktopsStrengths: updated };
    });
  };

  const handleFloorMeasurementChange = (index: number, value: string) => {
    setForm((prev) => {
      const updated = [...prev.floorMeasurements];
      updated[index].floorMeasurement = value;
      return { ...prev, floorMeasurements: updated };
    });
  };

  const handleHatchDimensionChange = (index: number, field: "dimensionl" | "dimensionw", value: string) => {
    setForm((prev) => {
      const updated = [...prev.hatchDimensions];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, hatchDimensions: updated };
    });
  };

  const handleHatchCoverStrengthChange = (index: number, value: string) => {
    setForm((prev) => {
      const updated = [...prev.hatchCoverStrengths];
      updated[index].strength = value;
      return { ...prev, hatchCoverStrengths: updated };
    });
  };

  const handleCementFeederHolesChange = (index: number, field: "numberDiameter" | "location", value: string) => {
    setForm((prev) => {
      const updated = [...prev.cementFeederHoles];
      updated[index][field] = value;
      return { ...prev, cementFeederHoles: updated };
    });
  };

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/bq_CargoArrangements`, {
        params: {
          imo_no: imoNumber,
          vessel_name: vesselName,
        },
      });
      const _formSql = result.data[0];
      const _form = mapSqlToBq5CargoArrangementsForm(_formSql);

      const num = Math.min(Number(_form.numberOfHolds) || 1, 9);
      const numhatches = Math.min(Number(_form.numberOfHatches) || 1, 20);

      const initializedForm = {
        ..._form,
        holdDimensions: _form.holdDimensions.slice(0, num),
        holdCapacities: Array.from({ length: num }, (_, i) => ({
          holdNumber: i + 1,
          grainCapacity: _form.holdCapacities?.[i]?.grainCapacity || "",
          baleCapacity: _form.holdCapacities?.[i]?.baleCapacity || "",
        })),
        tanktopsStrengths: Array.from({ length: num }, (_, i) => ({
          holdNumber: i + 1,
          strength: _form.tanktopsStrengths?.[i]?.strength || "",
        })),
        floorMeasurements: Array.from({ length: num }, (_, i) => ({
          holdNumber: i + 1,
          floorMeasurement: _form.floorMeasurements?.[i]?.floorMeasurement || "",
        })),
        hatchDimensions: _form.hatchDimensions.slice(0, numhatches),
        hatchCoverStrengths: Array.from({ length: numhatches }, (_, i) => ({
          hatchNumber: i + 1,
          strength: _form.hatchCoverStrengths?.[i]?.strength || "",
        })),
        cementFeederHoles: Array.from({ length: numhatches }, (_, i) => ({
          hatchNumber: i + 1,
          numberDiameter: _form.cementFeederHoles?.[i]?.numberDiameter || "",
          location: _form.cementFeederHoles?.[i]?.location || "",
        })),
      };

      setForm(initializedForm);
      setStoredForm(initializedForm);
      cacheInitialForm(initializedForm);
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-4">Cargo Arrangements</h1>
        <form onSubmit={handleSubmit}>
          {Bq5Holds(
            form,
            handleChange,
            handleHoldDimensionChange,
            handleHoldCapacityChange,
            handleTanktopsStrengthsChange,
            handleFloorMeasurementChange,
            toggleComments,
            isCommentsVisible,
            readonly,
          )}

          {Bq5DeckAndHatches(
            form,
            handleChange,
            handleHatchDimensionChange,
            handleHatchCoverStrengthChange,
            handleCementFeederHolesChange,
            toggleComments,
            isCommentsVisible,
            readonly,
          )}

          {Bq5Ballast(form, handleChange, toggleComments, isCommentsVisible, readonly)}

          {Bq5BWMS(form, handleChange, toggleComments, isCommentsVisible, readonly)}

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
